import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

// store
import { useStoreValue, storeSet, storeGet } from '../../store';

// components
import StudioTimeline from './components/StudioTimeline';
import RecSessionTimeline from './components/RecSessionTimeline';
import TimelineControlsWidget from '../../components/TimelineControlsWidget';
import MyStudioLeftPanel from './components/MyStudioLeftPanel';
import PinIcon from '../../icons/PinIcon';
import ButtonIconRadioFlat from '../../components/ButtonWrapper/components/ButtonIconRadioFlat';
import WizardIcon from '../../icons/WizardIcon';
import RollingArrowsIcon from '../../icons/RollingArrowsIcon';
import PatchIcon from '../../icons/PatchIcon';
import PenAiIcon from '../../icons/PenAiIcon';
import PinAiIcon from '../../icons/PinAiIcon';
import MoodAiIcon from '../../icons/MoodAiIcon';
import Tooltip from '../../components/Tooltip';
import CommCenterMyStudio from './components/CommCenterMyStudio';
import ModalStemSplitter from '../../components/ModalStemSplitter';
import ModalMoodCover from '../../components/ModalMoodCover';
import AiNotePopup from '../../components/AiNotePopup';
import useCacheProjectAudio from '../../hooks/useCacheProjectAudio';
import useTimelineNotes from '../../hooks/useTimelineNotes';
import { buildNoteExtra, NOTE_MODES } from '../../helpers/timelineNotes';
import ProjectService from '../../service/project-service';
import StemService from '../../service/stem-service';
import GenerationService from '../../service/generation-service';

const ProjectMyStudio = () => {
    const { projectId } = useParams();
    const selectedVideo = useStoreValue('selectedVideo');
    const myStudioLeftPanel = useStoreValue('myStudioLeftPanel');
    const stemSplitterSelection = useStoreValue('stemSplitterSelection');
    const generateInstrumentSelection = useStoreValue('generateInstrumentSelection');
    const aiNoteSelection = useStoreValue('aiNoteSelection');
    const penCanvasData = useStoreValue('penCanvasData');
    const lastProjectId = useRef(null);
    const prevSelectedVideoRef = useRef(null);

    // Button group state - only one can be selected at a time
    const [aiEditTool, setAiEditTool] = useState(null);

    // The AI job modals outlive the timeline selection that opened them: once the
    // request is queued the selection is cleared, but the modal stays up so the
    // queue below the form can show the job going through.
    const [stemModalOpen, setStemModalOpen] = useState(false);
    const [instrumentModalOpen, setInstrumentModalOpen] = useState(false);

    useEffect(() => {
        if (stemSplitterSelection) setStemModalOpen(true);
    }, [stemSplitterSelection]);

    useEffect(() => {
        if (generateInstrumentSelection) setInstrumentModalOpen(true);
    }, [generateInstrumentSelection]);

    /** Clears the timeline selection a queued request has consumed. */
    const clearAiSelection = useCallback((key) => {
        storeSet(key, null);
        storeSet('timelineEditingMode', '');
        setAiEditTool(null);
    }, []);

    // Sorgenti audio per lo Stem Splitter: i video del progetto.
    const [stemSources, setStemSources] = useState([]);
    useEffect(() => {
        if (!projectId) return undefined;
        let active = true;
        ProjectService.getProjectVideosAsync(projectId)
            .then(videos => {
                if (active) {
                    setStemSources((videos || []).map(v => ({ id: v.id, type: 'video', name: v.name || 'Video' })));
                }
            })
            .catch(() => {});
        return () => { active = false; };
    }, [projectId]);

    const handleButtonToggle = useCallback((value) => {
        const next = aiEditTool === value ? null : value;
        setAiEditTool(next);
        if (value === 'stemSplitter') {
            storeSet('timelineEditingMode', next === 'stemSplitter' ? 'stemSplitter' : '');
        }
        if (value === 'generateInstrument') {
            storeSet('timelineEditingMode', next === 'generateInstrument' ? 'generateInstrument' : '');
        }
        if (NOTE_MODES.includes(value)) {
            storeSet('timelineEditingMode', next ? value : '');
            if (!next) storeSet('aiNoteSelection', null);
        }
    }, [aiEditTool]);

    const handleResetPenCanvas = useCallback(() => {
        storeSet('penCanvasResetSignal', Date.now());
    }, []);

    const handleAiNoteSend = useCallback(async (note, selection, mood) => {
        const mode = aiEditTool;
        const tempId = `local_${Date.now()}`;

        // Pen has no timeline selection: its range is the one the strokes cover.
        const anchor = mode === 'penai'
            ? { layer: null, startPos: penCanvasData?.range?.start ?? null, endPos: penCanvasData?.range?.end ?? null }
            : { layer: selection?.layer ?? null, startPos: selection?.startPos ?? null, endPos: selection?.endPos ?? null };
        const canvasJson = mode === 'penai' ? (penCanvasData?.json ?? null) : null;
        const extra = buildNoteExtra(mode, { ...anchor, mood, canvasJson });

        const submission = {
            id: tempId,
            mode,
            note,
            mood: mood || null,
            ...anchor,
            canvasJson,
            timelineType: selection?.timelineType ?? 'studio',
            createdAt: new Date().toISOString(),
        };
        // Always read the list back from the store: the send refetches the saved
        // notes in between, and a closure copy would drop them.
        storeSet('aiNoteSubmissions', [...(storeGet('aiNoteSubmissions') || []), submission]);

        if (mode === 'penai') {
            storeSet('penCanvasResetSignal', Date.now());
        }

        storeSet('aiNoteSelection', null);
        storeSet('timelineEditingMode', '');
        setAiEditTool(null);

        let created = null;
        try {
            created = await storeSet('sendMessage', projectId, { body: note, extra }, 'TEAM');
        } catch {
            // sendMessage already showed the error
        }

        const rest = (storeGet('aiNoteSubmissions') || []).filter(s => s.id !== tempId);
        if (!created?.id) {
            // Not saved: drop the marker rather than show a note nobody else will see.
            storeSet('aiNoteSubmissions', rest);
            return;
        }
        const alreadyLoaded = rest.some(s => s.id === created.id);
        storeSet('aiNoteSubmissions', alreadyLoaded ? rest : [...rest, { ...submission, id: created.id }]);
    }, [aiEditTool, penCanvasData, projectId]);

    useEffect(() => {
        if (lastProjectId.current !== projectId) {
            if (lastProjectId.current) {
                storeSet('cleanupVideoCache', lastProjectId.current, null);
            }
            lastProjectId.current = projectId;
            storeSet('refetchCueSheet', projectId);
        }
    }, [projectId]);

    useCacheProjectAudio(projectId);
    useTimelineNotes(projectId);

    // Handle selectedVideo changes - cache selected video
    useEffect(() => {
        if (prevSelectedVideoRef.current !== selectedVideo) {
            if (prevSelectedVideoRef.current !== null && prevSelectedVideoRef.current !== 0) {
                storeSet('cleanupVideoCache', projectId, prevSelectedVideoRef.current);
            }
            if (selectedVideo && selectedVideo !== 0) {
                storeSet('cacheSelectedVideo', projectId);
            }
            prevSelectedVideoRef.current = selectedVideo;
        }
    }, [selectedVideo, projectId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (projectId) {
                storeSet('cleanupAudioCache', projectId);
            }
        };
    }, [projectId]);

    return (
        <>
            <div className="appDashboard appMyStudio">
                <div className="appDashboard__workbench timelineEditorAuxWorkbench">
                    <div className="appWorkbenchPanel">
                        <MyStudioLeftPanel/>
                    </div>
                </div>
                <div className="appDashboard__workbench timelineEditorWorkbench">
                    {myStudioLeftPanel !== 'rec' ? <StudioTimeline key={projectId} /> : <RecSessionTimeline />}
                </div>
                <CommCenterMyStudio/>
            </div>
            <div className="appDashboard__widgets appMyStudio__widgets">
                <div className="appWorkbenchPanel">
                    <div>
                        <Tooltip label="Stem Splitter">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('stemSplitter')}
                                value="stemSplitter"
                                isSquare={true}
                                checked={aiEditTool === 'stemSplitter'}>
                                <PinIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Generate Instrument">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('generateInstrument')}
                                value="generateInstrument"
                                isSquare={true}
                                checked={aiEditTool === 'generateInstrument'}
                                disabled={myStudioLeftPanel !== 'rec'}>
                                <WizardIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Rolling">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('rolling')}
                                value="rolling"
                                isSquare={true}
                                checked={aiEditTool === 'rolling'}>
                                <RollingArrowsIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Patch">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('patch')}
                                value="patch"
                                isSquare={true}
                                checked={aiEditTool === 'patch'}>
                                <PatchIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                    </div>

                    <TimelineControlsWidget />

                    <div className="aiNotePopup__anchor">
                        <Tooltip label="Pen">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('penai')}
                                value="penai"
                                isSquare={true}
                                color="var(--note)"
                                checked={aiEditTool === 'penai'}>
                                <PenAiIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Pin">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('pinai')}
                                value="pinai"
                                isSquare={true}
                                color="var(--moodcolor01)"
                                checked={aiEditTool === 'pinai'}>
                                <PinAiIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Mood">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('moodai')}
                                value="moodai"
                                isSquare={true}
                                color="var(--accent)"
                                checked={aiEditTool === 'moodai'}>
                                <MoodAiIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        {aiEditTool === 'penai' && (
                            <AiNotePopup
                                mode="penai"
                                icon={<PenAiIcon />}
                                color="var(--note)"
                                selection={aiNoteSelection}
                                onSend={handleAiNoteSend}
                                onReset={handleResetPenCanvas}
                                onClose={() => handleButtonToggle('penai')}
                            />
                        )}
                        {aiEditTool === 'pinai' && (
                            <AiNotePopup
                                mode="pinai"
                                icon={<PinAiIcon />}
                                color="var(--moodcolor01)"
                                selection={aiNoteSelection}
                                onSend={handleAiNoteSend}
                                onClose={() => handleButtonToggle('pinai')}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ModalStemSplitter
                projectId={projectId}
                kind="stems"
                visible={stemModalOpen}
                selection={stemSplitterSelection}
                sources={stemSources}
                onHide={() => {
                    setStemModalOpen(false);
                    clearAiSelection('stemSplitterSelection');
                }}
                onSubmit={async (data) => {
                    const sel = data || {};
                    const wanted = sel.types || [];

                    await StemService.createStemAsync(projectId, {
                        timelineFrom: sel.startPos ?? 0,
                        timelineTo: sel.endPos ?? 0,
                        dialogue: wanted.includes('dialogue'),
                        music: wanted.includes('music'),
                        sfx: wanted.includes('sfx'),
                        note: sel.note || null,
                        sourceType: sel.sourceType || null,
                        sourceId: sel.sourceId || null,
                    });

                    clearAiSelection('stemSplitterSelection');
                }}
            />
            <ModalMoodCover
                projectId={projectId}
                visible={aiEditTool === 'moodai' && !!aiNoteSelection}
                selection={aiNoteSelection}
                onHide={() => {
                    storeSet('aiNoteSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
            />
            <ModalStemSplitter
                title="Genera strumento"
                projectId={projectId}
                kind="generations"
                visible={instrumentModalOpen}
                selection={generateInstrumentSelection}
                sources={stemSources}
                onHide={() => {
                    setInstrumentModalOpen(false);
                    clearAiSelection('generateInstrumentSelection');
                }}
                onSubmit={async (data) => {
                    const sel = data || {};
                    const instruments = sel.types || [];

                    // Kits.ai converts one instrument per call, so one job each.
                    await Promise.all(instruments.map(instrument => (
                        GenerationService.createGenerationAsync(projectId, {
                            mode: 'instrument',
                            instrument,
                            prompt: sel.note || null,
                            sourceType: sel.sourceType || null,
                            sourceId: sel.sourceId || null,
                            timelineFrom: sel.startPos ?? null,
                            timelineTo: sel.endPos ?? null,
                        })
                    )));

                    clearAiSelection('generateInstrumentSelection');
                }}
            />
        </>
    )
}

export default ProjectMyStudio;